<template>
  <PrimaryModal @clickBackground="close()">
    <div
      class="problem-select-wrapper max-w-screen-lg box-border px-12 py-8 m-4 overflow-y-scroll text-gray-800 dark:text-dark-text"
      :class="{ 'custom-scrollbar': isLightMode(), 'custom-scrollbar-dark': !isLightMode() }"
      style="user-select: none"
    >
      <div v-for="(note, index) in note_jp" :key="index" class="pt-2 mb-10">
        <h1 class="text-3xl my-2 border-b-2 pl-2 border-gray-400 dark:border-dark-200"> {{ note.title }}: </h1>
        <ul>
          <li v-for="(des, index) in note.description" :key="index" class="description ml-12 text-lg">
            {{ des }}
          </li>
        </ul>
      </div>
    </div>
  </PrimaryModal>
</template>

<script lang="ts">
import { defineComponent, onUnmounted } from 'vue'

import { UpdateHistory } from '@/domain/entities/updateHistory/updateHistory'
import PrimaryModal from '@/presentation/components/Modal/PrimaryModal.vue'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'

export default defineComponent({
  components: { PrimaryModal },
  setup(props, ctx) {
    const modalStatePresenter = useModalStatePresenter()
    const { isLightMode } = useConfigStatePresenter()

    const close = () => {
      modalStatePresenter.setIsShowUpdateModal(false)
    }

    const hotKeys = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        close()
      }
    }

    document.addEventListener('keydown', hotKeys)

    onUnmounted(() => {
      document.removeEventListener('keydown', hotKeys)
    })

    const note_jp: UpdateHistory[] = []
    note_jp.push({
      title: '2024-12-15',
      description: ['アカウント登録機能を追加']
    })
    note_jp.push({
      title: '2024-11-29',
      description: ['解説・提出履歴のデザインを変更']
    })
    note_jp.push({
      title: '2024-11-19',
      description: ['テストコンソールのデザインを変更', 'ショートカットキーを「試す」「提出」に限定']
    })
    note_jp.push({
      title: '2024-10-12',
      description: ['UIデザインを変更']
    })
    note_jp.push({
      title: '2024-10-07',
      description: ['模範解答ページのUIを変更']
    })
    note_jp.push({
      title: '2024-9-19',
      description: ['レビューページのUIを変更']
    })
    note_jp.push({
      title: '2024-7-19',
      description: ['レビューリクエストの項目を追加', 'C++20, C++23 を追加']
    })
    note_jp.push({
      title: '2024-6-20',
      description: ['フリースペースモードを追加']
    })
    note_jp.push({
      title: '2024-5-11',
      description: ['レビュー機能をモーダルから行うように変更']
    })
    note_jp.push({
      title: '2024-2-5',
      description: ['レビュー機能を追加', '提出履歴のデザインを変更']
    })
    note_jp.push({
      title: '2023-11-21',
      description: ['ダッシュボードにコース進捗グラフを追加']
    })
    note_jp.push({
      title: '2023-10-23',
      description: ['ログイン無しでサイトが使用できるように変更']
    })
    note_jp.push({
      title: '2023-10-11',
      description: ['ダッシュボードのデザインを変更']
    })
    note_jp.push({
      title: '2023-06-23',
      description: ['C++, Java, Python の言語サポートを追加']
    })
    note_jp.push({
      title: '2023-06-06',
      description: ['問題検索パネルにヒートマップを追加']
    })
    note_jp.push({
      title: '2023-05-01',
      description: [
        'サイトツアーを追加',
        'エディタコンソールからジャッジの結果を削除',
        'ヘッダーが見切れる問題を修正',
        'テスト結果とジャッジ結果の通知位置を変更'
      ]
    })
    note_jp.push({
      title: '2023-03-27',
      description: ['ヘッダーとサイドメニューのデザインを変更']
    })
    note_jp.push({
      title: '2023-03-09',
      description: ['ダッシュボードに他ユーザーとの比較機能を追加']
    })
    note_jp.push({
      title: '2022-12-26',
      description: [
        '模範解答とのDiffを表示する機能を追加',
        '対応言語にPyPy3を追加',
        'ユーザーメニューにContact項目を追加'
      ]
    })
    note_jp.push({
      title: '2022-12-17',
      description: ['アリーナの問題を指定して提出できる機能を実装']
    })
    note_jp.push({
      title: '2022-11-26',
      description: ['問題の統計情報を追加']
    })
    note_jp.push({
      title: '2022-11-05',
      description: ['ライトモードのデザインを変更', 'ダークモードのデザインを変更', '問題検索エリアをフッタに移動']
    })
    note_jp.push({
      title: '2022-10-03',
      description: ['通知のキャプションを変更', '問題検索エリアを追加', 'ヘッダの高さを変更']
    })
    note_jp.push({
      title: '2022-09-16',
      description: ['フッタを追加', '前の問題ボタンの不具合を修正']
    })
    note_jp.push({
      title: '2022-09-15',
      description: ['通知を追加']
    })
    note_jp.push({
      title: '2022-08-25',
      description: [
        '試す・提出をユーザーがカスタムできるように変更',
        'ダークモードを追加',
        '更新履歴を追加',
        'ジャッジデータの貼付け後、エディタに遷移しアニメーションを追加'
      ]
    })
    note_jp.push({
      title: '2022-08-13',
      description: [
        'エディタのフォント拡大縮小を追加',
        '自動翻訳したときアイコンが文字になる問題を修正',
        '自動翻訳が無効になるよう変更',
        'ジャッジデータをコンソールに貼り付ける機能を追加',
        'ショートカットキーをCtrlベースに変更',
        'テストケースが前に選んでいた問題のままになってしまうバグを修正'
      ]
    })
    note_jp.push({
      title: '2022-08-05',
      description: [
        'ショートカットキーを追加',
        'ダッシュボードボタンをヘッダーに移動',
        '解説の初期表示言語を現在選択中の言語になるよう変更'
      ]
    })
    note_jp.push({
      title: '2022-07-26',
      description: ['AC数を視覚化するヒートマップを追加']
    })
    note_jp.push({
      title: '2022-07-16',
      description: ['ダッシュボードのバグを修正']
    })
    note_jp.push({
      title: '2022-07-15',
      description: ['ユーザーメニューにダッシュボードを追加', '入力の高さを修正', '追加問題に対応']
    })
    note_jp.push({
      title: '2022-06-20',
      description: [
        '前の問題ボタンを追加',
        '提出履歴、模範解答にページ番号を追記',
        '提出履歴、模範解答のロードタイミングを「問題変更後、サイドメニューから選択後、初回のみ」に変更',
        '提出履歴、模範解答のロードに合わせてアニメーションを追加',
        'テーマカラーをコース、チャレンジに合わせ変更',
        '言語セレクタのデザインを変更'
      ]
    })
    note_jp.push({
      title: '2022-06-07',
      description: [
        'コンソールのサンプルケースの結果を消去しテスト結果に統合',
        'コンソール入力欄の余白を消去',
        'コンソールのジャッジバッジのフォントサイズを変更'
      ]
    })
    note_jp.push({
      title: '2022-06-06',
      description: ['解説、提出履歴、模範解答のページ状態を保存']
    })
    note_jp.push({
      title: '2022-05-31',
      description: ['新規追加問題の次の問題に対応']
    })
    note_jp.push({
      title: '2022-05-18',
      description: [
        '問題IDを追加',
        '問題を変更した際に問題文を最上部までスクロールするよう変更',
        'コンソールにおいて結果を右側に分離して表示',
        'サンプルケースの結果を個別に表示',
        'サンプルケースの貼付けをプルダウンメニューに変更',
        '各ホバーテキストのデザインを変更',
        '追加問題の次の問題に対応'
      ]
    })
    note_jp.push({
      title: '2022-05-05',
      description: [
        'C#の模範解答が表示されない問題を修正',
        'policyがprivateの模範解答をクリック不可に変更',
        '解説のアイコンを変更',
        'ユーザーメニューの表示位置を修正'
      ]
    })
    note_jp.push({
      title: '2022-04-26',
      description: ['ヘッダーの高さを変更', '解説の位置を変更', 'ICEアイコンを消去', 'TimeLimit関連のテキストを消去']
    })
    note_jp.push({
      title: '2022-04-13',
      description: [
        'ファインダーリンクボタンを変更',
        'タブに縦スクロールアニメーションを追加',
        '問題文、提出履歴、模範解答、サンプルケースにロードアニメーションを追加',
        '次の問題ボタンを追加',
        'ヘルプボタンをユーザーメニューに移動',
        'テストケースダウンロードボタンでファイルを直にダウンロードするように変更',
        'ダウンロードボタン、アップロードボタンのデザインを変更',
        'テストケースの不具合を修正',
        '数式ライブラリの読み込みタイミングを変更',
        '問題文、提出履歴、模範解答のロードアニメーションをapi読み込み時のみに修正'
      ]
    })
    note_jp.push({
      title: '2022-04-02',
      description: ['ファインダー縦幅修正', 'ブックマークボタン追加']
    })
    note_jp.push({
      title: '2022-03-22',
      description: [
        'コンソールのデザインを変更',
        'サンプルケースアイコンを変更',
        'エラー、ジャッジアイコンを追加',
        'ホバーテキストを追加'
      ]
    })
    note_jp.push({
      title: '2022-03-16',
      description: ['ジャッジ、テストでコンソール内をオートスクロール', 'コンソールの高さを修正']
    })
    note_jp.push({
      title: '2022-03-08',
      description: [
        'ログイン画面を修正',
        'テストタブとジャッジタブを統一',
        'エディタタブを追加',
        '提出履歴でコードを表示するように変更',
        'タブの左右幅分割を修正'
      ]
    })
    note_jp.push({
      title: '2022-02-16',
      description: [
        '右側サイドメニューを追加',
        'エディタの右端のバグを修正',
        'タブの幅を変えるスクロールバーを変更',
        'タブの幅を変えるとき、問題文などが選択されないように修正'
      ]
    })
    note_jp.push({
      title: '2022-02-01',
      description: ['模範解答を追加', 'ジャッジタブの実行時間、テストケース名の表示を修正']
    })
    note_jp.push({
      title: '2022-01-15',
      description: [
        'テストタブでACしたときの空白を修正',
        'テストタブのエラーをcloseできるように変更',
        '細かな日本語化を実施',
        '解説デザインを変更',
        'sec KB check のデザインを変更'
      ]
    })
    note_jp.push({
      title: '2022-01-13',
      description: ['テストコンソールの下部余白を修正']
    })
    note_jp.push({
      title: '2022-01-09',
      description: [
        '解説のデザインを変更',
        '問題文のデザインを変更',
        'チャレンジ問題選択画面のカーソルを修正',
        'テストタブを修正',
        'ジャッジのJUDGE ID: XXXXX By XXX を JUDGE RESULTに変更',
        'Input Outputに横スクロールを追加'
      ]
    })
    note_jp.push({
      title: '2021-12-29',
      description: [
        'サンプルケースのボタンデザイン変更',
        'タブの高さを縮小',
        'PEケースのバグを修正',
        'timeの単位を修正'
      ]
    })
    note_jp.push({
      title: '2021-12-27',
      description: [
        '閉じるボタンを解説下部に追加',
        '問題セレクト画面でのカーソルを変更',
        'clearボタンを削除',
        'ErrorMessageタブを削除',
        'ウィンドウの変更可能幅を設定',
        'コンソールのデザインを変更',
        '問題分の sec KB ☑ の縮小'
      ]
    })
    note_jp.push({
      title: '2021-12-20',
      description: ['ジャッジケースを表示されるように変更', '解説を閉じられるように変更']
    })

    return { close, note_jp, isLightMode }
  }
})
</script>

<style scoped>
.problem-select-wrapper {
  width: 50vw;
  height: 70vh;
}

.description {
  list-style-type: disc;
}
</style>
